"use client";

import _Faq from "@/app/[locale]/(user-page)/faq/components/content/Faq";
import { APP_ROUTES } from "@/constants/routes";
import { Faq } from "@/models/faq";
import { paramFaq } from "@/models/Params";
import FaqServices from "@/services/main/faqServices";
import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import Link from "next/link";

export default function FaqList({
  lang,
  categories,
  noPaddingTop = false,
}: {
  lang?: string;
  categories?: number[];
  noPaddingTop?: boolean;
}) {
  const t = useTranslations("messages");
  const { data: faqs, isLoading } = useQuery({
    queryKey: categories ? ["faqs", lang, categories] : ["faqs", lang],
    queryFn: async () => {
      let params: paramFaq = {
        take: 99999,
      };
      if (categories && categories?.length > 0) {
        params = {
          ...params,
          categoryIds: categories,
        };
      }
      const result = await FaqServices.getAllFaq(params, lang);
      return result;
    },
    refetchOnWindowFocus: false,
  });

  return !!categories && (faqs?.length === 0 || !faqs) ? null : (
    <Box
      sx={{
        px: { xs: 4, sm: 8 }, // left + right
        pb: { xs: 4, sm: 8 }, // bottom
        pt: noPaddingTop ? 0 : { xs: 4, sm: 8 }, // top
        color: "black",
        height: "100%",
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "22px", sm: "32px" },
                fontWeight: 600,
                lineHeight: "48px",
                letterSpacing: "-3%",
                color: "var(--cimigo-blue)",
                fontFamily: "Montserrat",
                textAlign: "center",
              }}
              component="h1"
              translation-key="common_frequently_asked_questions"
            >
              {t("common_frequently_asked_questions")}
            </Typography>
          </Box>
          {/* FAQ List */}
          <Box
            sx={{
              height: 40,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flex: 1,
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                maxWidth: 950,
                minWidth: 300,
                mx: "auto",
                "& .Mui-expanded": {
                  margin: "0 !important",
                },
              }}
            >
              {faqs?.length > 0 ? (
                // Sử dụng Fragment để nhóm danh sách và nút "See all"
                <>
                  {faqs.map((item: any) => (
                    <Box key={item?.categoryName}>
                      <Typography
                        sx={{
                          fontSize: { xs: "16px", sm: "18px" },
                          fontWeight: 700,
                          color: "var(--cimigo-blue)",
                          mb: 1.5,
                          mt: 2,
                          letterSpacing: "0.5px",
                          fontFamily: "Montserrat",
                          textAlign: "left",
                          borderLeft: "4px solid var(--cimigo-blue)",
                          pl: 2,
                          background: "rgba(0, 123, 255, 0.04)",
                          borderRadius: "6px 0 0 6px",
                          display: "inline-block",
                          width: "fit-content",
                        }}
                      >
                        {item?.categoryName}
                      </Typography>
                      {item?.faqs?.map((faq: Faq) => (
                        <_Faq
                          key={faq.id}
                          title={faq.title}
                          description={faq.description}
                          tab={faq.id}
                        />
                      ))}
                    </Box>
                  ))}

                  {/* 2. Thêm liên kết "See all" ở đây */}
                  {(categories || noPaddingTop) && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        width: "100%",
                        mt: 2,
                      }}
                    >
                      <Typography
                        component={Link}
                        href={APP_ROUTES(lang).FAQ.to}
                        sx={{
                          color: "var(--cimigo-blue)",
                          fontWeight: 600,
                          fontSize: "18px",
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                        translation-key="common_see_all"
                      >
                        {t("common_see_all")}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Typography
                  translation-key="common_no_faqs_found"
                  align="center"
                  color="text.secondary"
                >
                  {t("common_no_faqs_found")}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
